import { Button } from "@arcadeai/design-system/components/ui/atoms/button";
import { ScrollArea } from "@arcadeai/design-system/components/ui/atoms/scroll-area";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@arcadeai/design-system/components/ui/atoms/sheet";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@arcadeai/design-system/components/ui/atoms/tooltip";
import { GatewayConnectSection } from "@arcadeai/ui-kit/mcp-gateway-connect";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { Code, ExternalLinkIcon } from "lucide-react";
import { useCallback } from "react";
import { useSession } from "@/lib/auth-client";
import { buildOrgProjectPath } from "@/lib/org-project-context/constants";
import { useOrgProjectRouteContext } from "@/lib/org-project-context/hooks";
import { useEnginePublicUrl } from "../../api/get-app-config";
import { useMcpGatewayConnectTelemetry } from "../../hooks/use-mcp-gateway-connect-telemetry";
import {
  MCP_GATEWAYS_ROUTE_ID,
  type McpGatewaysSearch,
} from "../../search-params";
import type { McpServer } from "../../types";

type ConnectDrawerProps = {
  gateway: McpServer;
};

/**
 * Connect sheet rendered per gateway row on the list page.
 *
 * Open state is fully URL-controlled via the `connect` search param:
 * `?connect=<gateway.id>` opens this drawer for the matching row, and
 * clearing the param closes it. The gateway create dialog uses this to deep-link the
 * user straight into the connect flow after creating a gateway.
 */
export function ConnectDrawer({ gateway }: ConnectDrawerProps) {
  const { connect } = useSearch({ from: MCP_GATEWAYS_ROUTE_ID });
  const navigate = useNavigate();
  const { data: session } = useSession();
  const enginePublicUrl = useEnginePublicUrl();
  const orgProject = useOrgProjectRouteContext();
  const { onConfigCopy, onDeepLinkClick } = useMcpGatewayConnectTelemetry();

  const open = connect === gateway.id;
  const userId = session?.user?.rawEmail ?? session?.user?.email;
  const apiKeysHref = buildOrgProjectPath("/api-keys", orgProject);

  const handleOpenChange = useCallback(
    (next: boolean) => {
      navigate({
        to: ".",
        search: (prev: McpGatewaysSearch) => ({
          ...prev,
          connect: next ? gateway.id : "",
        }),
        replace: true,
      });
    },
    [gateway.id, navigate]
  );

  return (
    <Sheet onOpenChange={handleOpenChange} open={open}>
      <TooltipProvider delay={200}>
        <Tooltip>
          <TooltipTrigger
            render={
              <Button
                aria-label="Connect to Client"
                onClick={() => handleOpenChange(true)}
                size="sm"
                variant="ghost"
              />
            }
          >
            <Code className="size-4" />
            Connect
          </TooltipTrigger>
          <TooltipContent>
            <p>Connect to Client</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
      <SheetContent className="w-full px-2">
        <ScrollArea className="h-full w-full">
          <SheetHeader>
            <SheetTitle className="mt-2">Connect to Client</SheetTitle>
            <SheetDescription>
              Connect this MCP gateway to your preferred client application.{" "}
              <a
                className="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground"
                href="https://docs.arcade.dev/en/get-started/mcp-clients"
                rel="noopener noreferrer"
                target="_blank"
              >
                Docs
                <ExternalLinkIcon className="size-3" />
              </a>
            </SheetDescription>
          </SheetHeader>
          <div className="mt-4 px-4 pb-4">
            <GatewayConnectSection
              apiKeysHref={apiKeysHref}
              enginePublicUrl={enginePublicUrl}
              gateway={gateway}
              onConfigCopy={onConfigCopy}
              onDeepLinkClick={onDeepLinkClick}
              userId={userId}
            />
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}
